import { useEffect, useRef } from 'react';
import { useOnlineStatus } from './use-online-status';
import { useSyncMachineService } from '@/contexts/sync-machine-context';
import { scheduleSync } from '@/stores/sync-orchestrator';
import { useAppSelector } from '@/stores/hooks';
import type { AppDispatch } from '@/stores/store';

interface SyncPollingOptions {
  /**
   * Interval (ms) used for the **currently open vault** when the tab is
   * in the foreground.  Default: 10 000 (10 s).
   */
  foregroundInterval?: number;
  /**
   * Interval (ms) used for **all other vaults** while the tab is visible,
   * and for all vaults when no specific vault is open.
   * Default: 30 000 (30 s).
   */
  backgroundInterval?: number;
  /**
   * Interval (ms) used for all vaults when the browser tab is hidden
   * (`document.visibilityState === 'hidden'`).
   * Default: 60 000 (60 s).
   */
  hiddenTabInterval?: number;
}

/**
 * Single polling loop for all vault sync machines.
 *
 * Replaces `useVaultSync` (10 s, all vaults via `syncVaultAllThunk`) and
 * `useVaultSyncCheck` (10 s, per vault via `syncReloadRemoteThunk`).
 *
 * Behaviour:
 * - On mount: immediately runs one cycle for every `idle` or `dirty` machine.
 * - Interval ticks call `scheduleSync` for all `idle` or `dirty` machines.
 * - The active vault (from Redux `currentVault`) uses the foreground interval;
 *   all other vaults use the background interval.
 * - When the browser tab is hidden the hidden-tab interval applies to all.
 * - Polling is suppressed entirely when the device is offline (machines will
 *   be in `paused` state anyway, so there is nothing to do).
 *
 * Mount **once** at the app root (e.g. inside `App`). The component that
 * previously called `useVaultSyncCheck` should be updated to no longer do so.
 *
 * @param dispatch The Redux `AppDispatch`, forwarded to `scheduleSync`.
 * @param options  Optional interval overrides.
 */
export function useSyncPolling(
  dispatch: AppDispatch,
  {
    foregroundInterval = 10_000,
    backgroundInterval = 30_000,
    hiddenTabInterval = 60_000,
  }: SyncPollingOptions = {},
): void {
  const service = useSyncMachineService();
  const isOnline = useOnlineStatus();
  const isAuthenticated = useAppSelector((state) => state.vault.auth.isAuthenticated);
  const activeVaultId = useAppSelector(
    (state) => state.vault.currentVault?.info.id,
  );

  // Keep a stable ref to the active vault ID so the interval callback always
  // sees the latest value without needing to be recreated on every navigation.
  const activeVaultIdRef = useRef(activeVaultId);
  useEffect(() => {
    activeVaultIdRef.current = activeVaultId;
  }, [activeVaultId]);

  // ── Immediate bootstrap cycle on mount ──────────────────────────────────
  useEffect(() => {
    if (!isOnline || isAuthenticated !== true) return;

    for (const [vaultId, machine] of service.listAll()) {
      if (machine.status === 'idle' || machine.status === 'dirty') {
        scheduleSync(vaultId, dispatch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, dispatch]);
  // Note: intentionally omitting `isOnline` so this only fires once on mount.

  // ── Polling interval ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isOnline || isAuthenticated !== true) {
      // All machines are paused when offline or unauthenticated — no point ticking
      return;
    }

    /**
     * On each tick, schedule a sync cycle for every machine that is ready.
     * The tick fires at the shortest applicable interval; within the tick we
     * decide whether each vault has reached *its* interval since it last ran.
     *
     * Simpler approach used here: fire at the foreground interval always, and
     * within the tick, apply back-off logic by comparing elapsed time.
     */
    const tick = () => {
      const hidden = document.visibilityState === 'hidden';

      for (const [vaultId, machine] of service.listAll()) {
        if (machine.status !== 'idle' && machine.status !== 'dirty') continue;

        const isActive = vaultId === activeVaultIdRef.current;
        const requiredInterval = hidden
          ? hiddenTabInterval
          : isActive
            ? foregroundInterval
            : backgroundInterval;

        const elapsed = Date.now() - getLastScheduled(vaultId);
        if (elapsed >= requiredInterval) {
          recordSchedule(vaultId);
          scheduleSync(vaultId, dispatch);
        }
      }
    };

    // Use the shortest interval as the base tick frequency so we detect when
    // either the foreground or background threshold is crossed.
    const baseInterval = Math.min(foregroundInterval, backgroundInterval);
    const timerId = setInterval(tick, baseInterval);

    return () => clearInterval(timerId);
  }, [
    isOnline,
    isAuthenticated,
    service,
    dispatch,
    foregroundInterval,
    backgroundInterval,
    hiddenTabInterval,
  ]);
}

// ── Per-vault last-scheduled tracking (module-level, not React state) ────────
// We track this outside React so the interval callback can update it without
// triggering re-renders.

const lastScheduledAt = new Map<string, number>();

function getLastScheduled(vaultId: string): number {
  return lastScheduledAt.get(vaultId) ?? 0;
}

function recordSchedule(vaultId: string): void {
  lastScheduledAt.set(vaultId, Date.now());
}
